import type { Key } from "../i18n/en";

/**
 * Cutting thresholds. Sources: Cricut's official minimum design size, and the
 * minimum stroke width Stahls' recommends for HTV.
 * Detail, and why we do NOT target 3 mm: paper `line-art-ia-a-svg-cortable-cricut`.
 */
export const THRESHOLDS_MM = {
  /** below this, failure is close to certain */
  fail: 1.0,
  /** recommended floor for letters and logos (1/16") */
  floor: 1.6,
  /** comfortable to weed without a fight */
  comfortable: 2.0,
} as const;

/** Workshop spec (1/8"). At shirt size it destroys any floral: not the goal here. */
export const PRODUCTION_SPEC_MM = 3.0;

/** Minimum feature size Cricut claims to support (1/4"). */
export const CRICUT_MIN_FEATURE_MM = 6.35;

export type Mode = "cut" | "draw";

export interface ModeSpec {
  id: Mode;
  /** Dictionary keys rather than text: core knows nothing about language. */
  labelKey: Key;
  hintKey: Key;
  /** target thickening in mm; null = do not thicken */
  targetMm: number | null;
}

/**
 * Cutting and drawing want OPPOSITE vectors.
 *
 * Cutting: the machine follows the edge of the shape, so the stroke has to be
 * thick or it tears while weeding.
 *
 * Drawing: the pen follows *every* path. A thick contour is drawn as TWO
 * parallel lines and the stroke comes out hollow. With a thin stroke (thinner
 * than the tip, 0.4-1 mm) the two lines merge and it reads as a hand stroke.
 */
export const MODES: Record<Mode, ModeSpec> = {
  cut: {
    id: "cut",
    labelKey: "params.mode.cut",
    hintKey: "mode.hint.cut",
    targetMm: THRESHOLDS_MM.floor,
  },
  draw: {
    id: "draw",
    labelKey: "params.mode.draw",
    hintKey: "mode.hint.draw",
    targetMm: null,
  },
};

/**
 * Which side to pin, given the shape of the design.
 *
 * Always pinning the width seems reasonable until a vertical design arrives: a
 * sprig with a 1:2.4 ratio at 180 mm wide comes out 43 cm tall, which fits no
 * shirt. The LONG side is pinned instead -- the one the person has in mind when
 * they say "make it 18 cm".
 */
export function preferredSide(pxWidth: number, pxHeight: number): "width" | "height" {
  return pxWidth >= pxHeight ? "width" : "height";
}

export const EXPLORE_5 = {
  /** maximum cut width without a mat */
  maxCutWidthMm: 297,
  /** minimum material length for the rollers to grip */
  minMaterialLengthMm: 150,
} as const;

/** A warning if the design does not fit the machine, or null if it does. */
export interface FitWarning {
  key: Key;
  vars: Record<string, string | number>;
}

export function fitWarning(mmWidth: number, mmHeight: number): FitWarning | null {
  const short = Math.min(mmWidth, mmHeight);
  if (short > EXPLORE_5.maxCutWidthMm) {
    return {
      key: "fit.tooWide",
      vars: { short: short.toFixed(0), max: EXPLORE_5.maxCutWidthMm },
    };
  }
  const long = Math.max(mmWidth, mmHeight);
  if (long > 400) {
    return { key: "fit.tooLong", vars: { long: long.toFixed(0) } };
  }
  return null;
}
